<template>
  <transition
    :enterClass="$style.fadeUpEnter"
    :enterActiveClass="$style.fadeUpEnterActive"
    :leaveActiveClass="$style.fadeUpLeaveActive"
    :leaveToClass="$style.fadeUpLeaveTo"
  >
    <div
      v-if="message || $slots.default"
      v-show="showAlert"
      :class="[$style[type], $style.loginAlert, alignClass]"
    >
      <div
        :class="$style.iconContainer"
        v-if="!centered"
      >
        <i :class="[iconClass, $style.alertIcon]" />
      </div>
      <CustomError
        v-if="isCustomErrorMessage"
        :errorMessage="message"
        @resolved="onParameterizedErrorResolved"
      />
      <div v-else-if="!$slots.default">
        {{ message }}
      </div>
      <slot v-else />
      <div
        v-if="resolvedCorrelationId"
        :class="$style.correlationId"
        data-test-id="LoginAlert__correlationId"
      >
        <span :class="$style.correlationLabel">Error ID:</span>
        <span :class="$style.correlationValueRow">
          <span :class="$style.correlationValue">{{ resolvedCorrelationId }}</span>
          <button
            type="button"
            :class="$style.correlationCopy"
            aria-label="Copy error ID"
            data-test-id="LoginAlert__correlationIdCopy"
            @click="copyCorrelationId"
          >
            <svg
              :class="$style.correlationCopyIcon"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                :d="copyIconPath"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </span>
        <span
          v-if="correlationCopied"
          :class="$style.correlationCopied"
          data-test-id="LoginAlert__correlationIdCopied"
        >
          Copied
        </span>
        <span :class="$style.correlationHelp">
          If the issue continues, share this Error ID with your IT admin.
        </span>
      </div>
    </div>
  </transition>
</template>

<script>
import { LoginConsts } from 'client/util/Constants';
import Util from 'client/util/Util';

import CustomError from './CustomError.vue';

const { error, success } = LoginConsts.alertTypes;

export default {
  name: 'Alert',
  components: {
    CustomError,
  },
  props: {
    // Determines whether the alert uses a centered text style
    centered: {
      type: Boolean,
      default: false,
    },

    // Optional CAP correlation ID; falls back to route query / custom error JSON
    correlationId: {
      type: String,
      default: '',
    },

    // The name of the error to display; if empty, alert is hidden
    message: {
      type: String,
      default: '',
    },

    // Determines what styles to use for the alert
    type: {
      type: String,
      default: error,
    },
  },
  data() {
    return {
      parameterizedErrorResolved: true,
      correlationCopied: false,
      // ClipboardDocument outline path; kept in script for max-len lint.
      copyIconPath: [
        'M8 7.5V5.25A2.25 2.25 0 0 1 10.25 3h8.5A2.25 2.25 0 0 1 21 5.25v8.5',
        'A2.25 2.25 0 0 1 18.75 16H16.5M5.25 8h8.5A2.25 2.25 0 0 1 16 10.25v8.5',
        'A2.25 2.25 0 0 1 13.75 21h-8.5A2.25 2.25 0 0 1 3 18.75v-8.5',
        'A2.25 2.25 0 0 1 5.25 8Z',
      ].join(''),
    };
  },
  watch: {
    message: {
      immediate: true,
      handler() {
        this.parameterizedErrorResolved = !this.parameterizedErrorDetails;
        this.correlationCopied = false;
      },
    },
  },
  computed: {
    parameterizedErrorDetails() {
      if (typeof this.message !== 'string' || !this.message) {
        return null;
      }
      return Util.parseParameterizedErrorDetails(this.message);
    },
    showAlert() {
      if (!this.message && !this.$slots.default) {
        return false;
      }
      if (this.parameterizedErrorDetails) {
        return this.parameterizedErrorResolved;
      }
      return true;
    },
    alignClass() {
      if (this.type === error) {
        return this.$style.loginAlertCenter;
      }
      return this.centered ? this.$style.loginAlertCenter : this.$style.loginAlertLeft;
    },
    iconClass() {
      return this.type === success ? 'jc-actions-check' : 'jc-actions-alert';
    },
    isCustomErrorMessage() {
      if (!this.message) return false;
      if (this.parameterizedErrorDetails) {
        return true;
      }
      try {
        const message = Util.tryParseJson(this.message);
        // Check if the message is a custom error
        return Util.isCustomErrorMessage(message);
      } catch (e) {
        return false;
      }
    },
    resolvedCorrelationId() {
      const fromProp = typeof this.correlationId === 'string' ? this.correlationId.trim() : '';
      if (fromProp) {
        return fromProp;
      }
      const routeError = this.$route && this.$route.query
        ? this.$route.query.error
        : undefined;
      const isCapDenyRoute = routeError === 'policyDenial' || routeError === 'access_denied';
      let parsedMessage;
      if (typeof this.message === 'string' && this.message) {
        try {
          parsedMessage = Util.tryParseJson(this.message);
        } catch (e) {
          // ignore
        }
      }
      let isCustomCapDeny = false;
      try {
        isCustomCapDeny = Util.isCustomErrorMessage(parsedMessage);
      } catch (e) {
        // ignore malformed custom-error payloads
      }
      const isCapDenyMessage = isCapDenyRoute
        || this.message === Util.genericPolicyDenialMessage
        || isCustomCapDeny;
      const fromRoute = this.$route && this.$route.query
        ? this.$route.query.correlation_id
        : undefined;
      const hasRouteCorrelationId = typeof fromRoute === 'string' && fromRoute.trim();
      const hasUnclassifiedRouteError = !routeError && typeof this.message === 'string' && this.message;
      if ((isCapDenyMessage || hasUnclassifiedRouteError) && hasRouteCorrelationId) {
        return fromRoute.trim();
      }
      if (typeof this.message === 'string' && this.message) {
        const parameterized = Util.parseParameterizedErrorDetails(this.message);
        if (parameterized && parameterized.type === 'auth-policy' && parameterized.correlation_id) {
          return parameterized.correlation_id;
        }
        const id = parsedMessage && parsedMessage.correlation_id;
        if (isCustomCapDeny && typeof id === 'string' && id.trim()) {
          return id.trim();
        }
      }
      return '';
    },
  },
  methods: {
    onParameterizedErrorResolved() {
      this.parameterizedErrorResolved = true;
    },
    copyCorrelationId() {
      if (!this.resolvedCorrelationId) {
        return;
      }
      const text = this.resolvedCorrelationId;
      const onCopied = () => {
        this.correlationCopied = true;
        window.setTimeout(() => {
          this.correlationCopied = false;
        }, 2000);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(onCopied).catch(() => {
          this.copyCorrelationIdWithFallback(text, onCopied);
        });
        return;
      }
      this.copyCorrelationIdWithFallback(text, onCopied);
    },
    copyCorrelationIdWithFallback(text, onCopied) {
      let tempTextarea;
      try {
        tempTextarea = document.createElement('textarea');
        tempTextarea.value = text;
        tempTextarea.setAttribute('readonly', '');
        tempTextarea.style.position = 'fixed';
        tempTextarea.style.top = '-9999px';
        document.body.appendChild(tempTextarea);
        tempTextarea.select();
        const copied = document.execCommand('copy');
        if (copied) {
          onCopied();
        }
      } catch (e) {
        // ignore clipboard failures
      } finally {
        if (tempTextarea && tempTextarea.parentNode) {
          tempTextarea.parentNode.removeChild(tempTextarea);
        }
      }
    },
  },
};
</script>

<style module>
@value fadeTransition from 'css/transitions.css';
@value (jcGrey900, jcError, jcError100, jcSuccess, jcSuccess100) from 'css/brand-colors.css';
@value lightShadow from 'css/shadows.css';

.loginAlert {
  border-radius: 3px;
  box-shadow: lightShadow;
  color: jcGrey900;
  font-size: 0.75rem;
  font-weight: 600;
  margin-top: 1rem;
  position: relative;
}

.error {
  background: jcError100;
  border-left: 5px solid jcError;
}

.success {
  background: jcSuccess100;
  border-left: 5px solid jcSuccess;
}

.loginAlertLeft {
  border-left: 0;
  padding: 1rem;
  text-align: left;
}

.loginAlertCenter {
  border-left: 0;
  padding: 17.5px 30px;
  text-align: center;
}

.iconContainer {
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 45px;
}

.alertIcon {
  font-size: 21px;
  left: 50%;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
}

.fadeUpEnterActive,
.fadeUpLeaveActive {
  transition: fadeTransition;
}

.fadeUpEnter,
.fadeUpLeaveTo {
  opacity: 0;
  transform: translate(20px, 0);
}

.correlationId {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  font-size: 11px;
  font-weight: 400;
  justify-content: center;
  line-height: 1.3;
  margin-top: 0.4rem;
}

.correlationLabel {
  flex-shrink: 0;
  margin-right: 0.35rem;
  opacity: 0.9;
}

.correlationValueRow {
  align-items: center;
  display: inline-flex;
  max-width: 100%;
  min-width: 0;
}

.correlationValue {
  font-family: monospace;
  font-size: 10px;
  font-weight: 400;
  margin-right: 0.25rem;
  overflow-wrap: anywhere;
}

.correlationCopy {
  align-items: center;
  background: transparent;
  border: 0;
  color: inherit;
  cursor: pointer;
  display: inline-flex;
  flex-shrink: 0;
  opacity: 0.85;
  padding: 0;
}

.correlationCopyIcon {
  display: block;
  height: 12px;
  width: 12px;
}

.correlationCopied {
  font-size: 10px;
  font-style: italic;
  font-weight: 400;
  margin-left: 0.35rem;
}

.correlationHelp {
  flex-basis: 100%;
  font-size: 10px;
  font-weight: 400;
  margin-top: 0.2rem;
}
</style>
